'use client';

import Image from 'next/image';
import Link from 'next/link';

import { Instagram, MessageCircle, Twitter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Bilingual copy is kept inline (EN + AR) to match the landing page body
// (index.tsx) — tenant-specific marketing copy stays self-contained rather
// than polluting the shared `landing.json` namespace. RTL direction flips
// automatically from the <html dir> set in app/layout.tsx for the `ar` locale.
type Bilingual = { en: string; ar: string };

const footerLinks: { label: Bilingual; href: string }[] = [
  { label: { en: 'About', ar: 'عن الأكاديمية' }, href: '/#about' },
  { label: { en: 'Programs', ar: 'البرامج' }, href: '/#programs' },
  { label: { en: 'Facilities', ar: 'المرافق' }, href: '/#facilities' },
  { label: { en: 'Location', ar: 'الموقع' }, href: '/#location' },
  {
    label: { en: 'Privacy Policy', ar: 'سياسة الخصوصية' },
    href: '/privacy-policy',
  },
  {
    label: { en: 'Terms of Service', ar: 'الشروط والأحكام' },
    href: '/terms-of-service',
  },
];

export function NetAcademyFooter() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;

  return (
    <footer className="relative overflow-hidden bg-[var(--net-ink)] pb-10 pt-20">
      {/* Oversize wordmark behind everything */}
      <div className="pointer-events-none absolute inset-x-0 -bottom-10 select-none text-center">
        <span
          className="font-net-display block text-[18vw] leading-[0.85] text-white/[0.04]"
          aria-hidden
        >
          NET ACADEMY
        </span>
      </div>

      <div className="relative mx-auto grid max-w-[1280px] grid-cols-1 gap-14 px-6 sm:px-10 lg:grid-cols-12 lg:gap-10 lg:px-14">
        {/* Mark + statement */}
        <div className="lg:col-span-5">
          <Image
            src="/landing-pages/netacademy/images/logo-header-white.svg"
            alt="NET Tennis Academy"
            width={180}
            height={64}
            className="h-12 w-auto"
          />
          <p className="font-net-body mt-6 max-w-md text-base leading-relaxed text-white/70">
            {isAr
              ? 'مجتمع لعشّاق التنس في الدرعية. سبعة ملاعب بمواصفات عالمية، وفريق تدريب متخصص، وبرامج لجميع الأعمار والمستويات.'
              : 'A community for tennis lovers in Diriyah. Seven international-spec courts, a dedicated coaching team, and programs for every age and level.'}
          </p>

          <div className="mt-8 flex items-center gap-4">
            <a
              href="https://instagram.com/nettennis_sa"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[var(--net-lime)] hover:bg-[var(--net-lime)] hover:text-[var(--net-ink)]"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://twitter.com/nettennis_sa"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X / Twitter"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[var(--net-lime)] hover:bg-[var(--net-lime)] hover:text-[var(--net-ink)]"
            >
              <Twitter className="h-5 w-5" />
            </a>
            <a
              href="https://wa.me/966554426646"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[var(--net-lime)] hover:bg-[var(--net-lime)] hover:text-[var(--net-ink)]"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Sitemap */}
        <div className="lg:col-span-3">
          <p className="font-net-label mb-4 text-[10px] text-[var(--net-lime)]">
            {isAr ? 'خريطة الموقع' : 'Sitemap'}
          </p>
          <ul className="space-y-3">
            {footerLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="font-net-body text-sm text-white/80 transition-colors hover:text-[var(--net-lime)]"
                >
                  {isAr ? link.label.ar : link.label.en}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Visit */}
        <div className="lg:col-span-4">
          <p className="font-net-label mb-4 text-[10px] text-[var(--net-lime)]">
            {isAr ? 'زورونا' : 'Visit'}
          </p>
          <address className="not-italic">
            <p className="font-net-body text-sm leading-relaxed text-white/80">
              {isAr ? (
                <>
                  أكاديمية نت للتنس
                  <br />
                  الدرعية الجديدة، الدرعية 13734
                  <br />
                  الرياض، المملكة العربية السعودية
                </>
              ) : (
                <>
                  NET Tennis Academy
                  <br />
                  PGWF+VG9, Al Diriyah Al Jadidah
                  <br />
                  Diriyah 13734, Saudi Arabia
                </>
              )}
            </p>
            <p className="font-net-body mt-4 text-sm text-white/80">
              {isAr ? (
                <>
                  الأحد — الخميس · 6–9 ص · 4 م – 12 ص
                  <br />
                  الجمعة — السبت · 6–10 ص · 4 م – 12 ص
                </>
              ) : (
                <>
                  Sun – Thu · 6–9 AM · 4 PM – 12 AM
                  <br />
                  Fri – Sat · 6–10 AM · 4 PM – 12 AM
                </>
              )}
            </p>
            <a
              href="tel:+966554426646"
              className="font-net-body mt-4 inline-block text-sm text-[var(--net-lime)] transition-colors hover:text-[var(--net-lime-soft)]"
              dir="ltr"
            >
              +966 55 442 6646
            </a>
          </address>
        </div>
      </div>

      <div className="relative mx-auto mt-16 max-w-[1280px] border-t border-white/10 px-6 pt-8 sm:px-10 lg:px-14">
        <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="text-xs text-white/50">
            {isAr ? (
              <>
                <span dir="ltr">© {new Date().getFullYear()}</span> أكاديمية نت
                للتنس. جميع الحقوق محفوظة.
              </>
            ) : (
              <>
                © {new Date().getFullYear()} NET Tennis Academy. All rights
                reserved.
              </>
            )}
          </p>
          <p className="text-xs text-white/50">
            {isAr ? 'صُنع في السعودية 🇸🇦' : 'Made in Saudi 🇸🇦'}
            <span className="mx-2">•</span>
            {isAr ? 'مشغّل بواسطة ' : 'Powered by '}
            <a
              href="https://askflow.sa"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--net-lime)] transition-colors hover:opacity-80"
            >
              askflow.sa
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
